// useAskPrice.js
import React, { useState, useCallback } from "react";
import './prompt-modal.css'; // Import the shared CSS
import ButtonBasic from "../buttonBasic/view";

// Componente interno Modal
export function CarItemModal({
  isOpen,
  defaultValue,
  defaultAmount,
  defaultDescription,
  isService = false,
  focusField,
  itemName,
  onConfirm,
  onCancel,
}) {
  const [value, setValue] = useState(defaultValue ?? "");
  const [amount, setAmount] = useState(defaultAmount ?? "");
  const [description, setDescription] = useState(defaultDescription ?? "");

  const valueRef = React.useRef(null);
  const amountRef = React.useRef(null);
  const descriptionRef = React.useRef(null);

  // Si el defaultValue o defaultAmount cambian cuando reabre el modal, actualizarlos y enfocar el campo correcto
  React.useEffect(() => {
    if (isOpen) {
      setValue(defaultValue ?? "");
      setAmount(defaultAmount ?? "");
      setDescription(defaultDescription ?? "");
      
      setTimeout(() => {
        if (focusField === "description" && descriptionRef.current) {
          descriptionRef.current.focus();
          descriptionRef.current.select();
        } else if (focusField === "value" && valueRef.current) {
          valueRef.current.focus();
          valueRef.current.select();
        } else if (focusField === "amount" && amountRef.current) {
          amountRef.current.focus();
          amountRef.current.select();
        }
      }, 50);
    }
  }, [isOpen, defaultValue, defaultAmount, defaultDescription, focusField]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(parseFloat(value), parseFloat(amount), description.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const adjustAmount = (delta) => {
    setAmount(prev => {
      const current = parseFloat(prev) || 0;
      const next = current + delta;
      return next > 0 ? String(next) : "1";
    });
  };

  const showDescriptionField = Boolean(isService);

  return (
    <div className="modal-overlay">
      <div className="modal-content-prompt">
        <h4 className="modal-prompt-title">Modificar Item</h4>
        {itemName && <p className="modal-prompt-subtitle">{itemName}</p>}
        
        <div className="modal-prompt-body">
          {showDescriptionField && (
            <div className="prompt-field-group">
              <span className="prompt-field-label">Descripción del Servicio:</span>
              <textarea
                ref={descriptionRef}
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Detalle o descripción personalizada..."
                className="prompt-field-textarea"
              />
            </div>
          )}

          <div className="prompt-field-group">
            <span className="prompt-field-label">Precio:</span>
            <div className="prompt-input-wrapper price-input-wrapper">
              <span className="prompt-input-prefix">$</span>
              <input
                ref={valueRef}
                type="number"
                step="0.01"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="prompt-field-input"
              />
            </div>
          </div>
          
          <div className="prompt-field-group">
            <span className="prompt-field-label">Cantidad:</span>
            <div className="prompt-quantity-wrapper">
              <button 
                type="button" 
                className="quantity-btn dec-btn"
                onClick={() => adjustAmount(-1)}
              >
                -
              </button>
              <input
                ref={amountRef}
                type="number"
                step="1"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={handleKeyDown}
                className="prompt-field-input quantity-input"
              />
              <button 
                type="button" 
                className="quantity-btn inc-btn"
                onClick={() => adjustAmount(1)}
              >
                +
              </button>
            </div>
          </div>
        </div>
        
        <div className="button-group-prompt"> 
          <ButtonBasic className="prompt-btn-cancel" onClick={onCancel}>Cancelar</ButtonBasic>
          <ButtonBasic className="prompt-btn-accept" onClick={handleConfirm}>Aceptar</ButtonBasic> 
        </div>
      </div>
    </div>
  );
}

export function useUpdateCarItem() {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState("");
  const [defaultAmount, setDefaultAmount] = useState("");
  const [defaultDescription, setDefaultDescription] = useState("");
  const [isService, setIsService] = useState(false);
  const [focusField, setFocusField] = useState("amount");
  const [itemName, setItemName] = useState("");
  const [resolveFn, setResolveFn] = useState(null);
  const [rejectFn, setRejectFn] = useState(null);

  const askUpdateCarItem = useCallback((value, amount, field = "amount", name = "", description = undefined, isServiceItem = false) => {
    return new Promise((resolve, reject) => {
      setDefaultValue(value);
      setDefaultAmount(amount);
      setDefaultDescription(description !== undefined ? description : "");
      setIsService(Boolean(isServiceItem));
      setFocusField(field);
      setItemName(name);
      setResolveFn(() => resolve);
      setRejectFn(() => reject);
      setIsOpen(true);
    });
  }, []);

  const onConfirm = (newValue, newAmount, newDescription) => {
    if (resolveFn) resolveFn({ value: newValue, amount: newAmount, description: newDescription });
    cleanup();
  };

  const onCancel = () => {
    if (rejectFn) rejectFn(new Error("cancelled"));
    cleanup();
  };

  const cleanup = () => {
    setIsOpen(false);
    setDefaultValue("");
    setDefaultAmount("");
    setDefaultDescription("");
    setIsService(false);
    setFocusField("amount");
    setItemName("");
    setResolveFn(null);
    setRejectFn(null);
  };

  return {
    askUpdateCarItem,
    UpdateCarItemModalComponent: (
      <CarItemModal
        isOpen={isOpen}
        defaultValue={defaultValue}
        defaultAmount={defaultAmount}
        defaultDescription={defaultDescription}
        isService={isService}
        focusField={focusField}
        itemName={itemName}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    ),
  };
}
